import { useToast } from '@chakra-ui/react';
import { InPageSurveyQuestionType } from '../common/libs/inPageSurveyQuestionType';
import { InPageSurveyAnswerStatus } from '../common/libs/inPageSurveyAnswerStatus';
import useApiClient from '../hooks/useApiClient';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { InPageSurveyUiType } from '../common/libs/inPageSurveyUiType';

export type InPageSurvey = {
  surveyId: number;
  uiType: InPageSurveyUiType;
  title: string | null;
  numberOfQuestions: number;
  questions: SurveyQuestion[];
  logoImgUrl: string | null;
};

export type SurveyChoice = {
  choiceNumber: number;
  choiceName: string;
};

export type SurveyQuestion = {
  questionNumber: number;
  questionType: InPageSurveyQuestionType;
  question: string;
  insertPosition: number;
  choices: SurveyChoice[] | null;
  answerStatus: InPageSurveyAnswerStatus | null;
};

export type InPageSurveyFormValues = {
  surveyId: number;
  questionNumber: number;
  answer: number | number[] | string | null;
};

/**
 * インページサーベイの回答・スキップ処理のロジックを提供するカスタムフック
 */
export default function useInPageSurvey() {
  const { apiClient } = useApiClient();
  const toast = useToast();

  /**
   * 回答処理
   *
   * @param surveyId サーベイID
   * @param questionNumber 設問番号
   * @param answer 回答内容
   */
  const answerSurvey = async ({
    surveyId,
    questionNumber,
    answer,
  }: {
    surveyId: number;
    questionNumber: number;
    answer: number | number[] | string;
  }) => {
    try {
      sendAnalyticsEvent(
        'slide',
        'answer',
        `survey-${surveyId}-q${questionNumber}`,
      );

      const values: InPageSurveyFormValues = {
        surveyId,
        questionNumber,
        answer: Array.isArray(answer) ? answer.join(',') : answer,
      };
      const res = await apiClient('/api/in-page-survey/answer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });
      if (res.ok) {
        toast({
          title: 'ご回答ありがとうございました',
          status: 'success',
          variant: 'subtle',
          position: 'bottom',
        });
      } else {
        throw new Error();
      }
    } catch (e) {
      toast({
        title: '回答の送信に失敗しました。時間をおいて再度お試しください。',
        status: 'error',
        variant: 'subtle',
        position: 'bottom',
      });
    }
  };

  /**
   * 回答のスキップ処理（現状、Gridモードでは不使用）
   *
   * @param survey サーベイ情報
   * @param question 質問情報
   */
  const skipSurvey = async ({
    surveyId,
    questionNumber,
  }: {
    surveyId: number;
    questionNumber: number;
  }) => {
    try {
      sendAnalyticsEvent(
        'slide',
        'skip',
        `survey-${surveyId}-q${questionNumber}`,
      );

      const values: InPageSurveyFormValues = {
        surveyId,
        questionNumber,
        answer: null,
      };

      const res = await apiClient('/api/in-page-survey/skip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        throw new Error();
      }
    } catch (e) {
      toast({
        title: '回答のスキップに失敗しました。',
        status: 'error',
        variant: 'subtle',
        position: 'bottom',
      });
    }
  };

  return {
    answerSurvey,
    skipSurvey,
  };
}
